/**
 * Daily Word (Wordle) - Main Container Component
 *
 * Orchestrates: mode selection -> game play -> completion.
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { WordleEngine } from '../WordleEngine';
import { WordleBoard } from './WordleBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { WordleErrorHandler } from '../services/WordleErrorHandler';
import { WordleStatisticsService } from '../services/WordleStatisticsService';
import { WordlePersistence } from '../services/WordlePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import {
  WordleGameState,
  WordleGameMode,
  WordleGameProps,
  WordleGameResult,
} from '../types';
import './Wordle.css';

type GameView = 'mode-select' | 'playing' | 'completed';

const WordleGameInner: React.FC<WordleGameProps> = ({ onExit }) => {
  const { isAuthenticated } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [gameState, setGameState] = useState<WordleGameState | null>(null);
  const [timer, setTimer] = useState(0);
  const [hardMode, setHardMode] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [shakeRow, setShakeRow] = useState(false);

  // Refs
  const engineRef = useRef<WordleEngine>(new WordleEngine());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const gameSessionIdRef = useRef<string | null>(null);

  // Services (lazy init)
  const errorHandlerRef = useRef<WordleErrorHandler | null>(null);
  const statsServiceRef = useRef<WordleStatisticsService | null>(null);
  const persistenceRef = useRef<WordlePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new WordleErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new WordleStatisticsService(statsManager);

      persistenceRef.current = new WordlePersistence();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize Wordle services:', e);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Timer management
  useEffect(() => {
    if (view === 'playing' && gameState?.gameStatus === 'playing') {
      timerRef.current = setInterval(() => {
        setTimer(t => t + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [view, gameState?.gameStatus]);

  // Toast auto-dismiss
  useEffect(() => {
    if (toastMessage) {
      const timeout = setTimeout(() => setToastMessage(null), 2000);
      return () => clearTimeout(timeout);
    }
  }, [toastMessage]);

  // Shake animation reset
  useEffect(() => {
    if (shakeRow) {
      const timeout = setTimeout(() => setShakeRow(false), 600);
      return () => clearTimeout(timeout);
    }
  }, [shakeRow]);

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
  }, []);

  const startGame = useCallback((mode: WordleGameMode) => {
    const engine = engineRef.current;
    const persistence = persistenceRef.current;

    // Check if daily is already completed
    if (mode === 'daily' && persistence?.isDailyCompleted()) {
      const completedState = persistence.getCompletedDailyState();
      if (completedState) {
        setGameState(completedState);
        setView('completed');
        showToast('Already completed today!');
        return;
      }
    }

    // Try to resume a saved game
    if (persistence) {
      const saved = persistence.loadGameState(mode);
      if (saved && saved.gameState.gameStatus === 'playing') {
        setGameState(saved.gameState);
        setTimer(saved.gameState.timer);
        setView('playing');
        return;
      }
    }

    // Start a new game
    const state = engine.createGame(mode, hardMode);
    setGameState(state);
    setTimer(0);
    setView('playing');

    // Track session
    const sessionId = gameSessionManager.current.startGameSession('wordle');
    gameSessionIdRef.current = sessionId;
  }, [hardMode, showToast]);

  const handleKeyPress = useCallback((key: string) => {
    if (!gameState || gameState.gameStatus !== 'playing') return;

    const engine = engineRef.current;

    if (key === 'ENTER') {
      const [newState, error] = engine.submitGuess(gameState);

      if (error) {
        showToast(error);
        setShakeRow(true);
        return;
      }

      const stateWithTimer = { ...newState, timer };
      setGameState(stateWithTimer);

      // Save progress
      persistenceRef.current?.saveGameState(stateWithTimer);

      // Handle game end
      if (stateWithTimer.gameStatus === 'won' || stateWithTimer.gameStatus === 'lost') {
        handleGameEnd(stateWithTimer);
      }
    } else if (key === 'BACKSPACE') {
      setGameState(engine.removeLetter(gameState));
    } else if (/^[A-Z]$/.test(key)) {
      setGameState(engine.addLetter(gameState, key));
    }
  }, [gameState, timer, showToast]);

  const handleGameEnd = useCallback((state: WordleGameState) => {
    // Record stats
    if (statsServiceRef.current) {
      const result: WordleGameResult = {
        won: state.gameStatus === 'won',
        guessCount: state.guesses.length,
        mode: state.mode,
        puzzleNumber: state.puzzleNumber,
        timeSeconds: timer,
        hardMode: state.hardMode,
        date: new Date().toISOString().split('T')[0],
      };
      statsServiceRef.current.recordGameResult(result);
    }

    // Mark daily as completed
    if (state.mode === 'daily' && state.puzzleNumber !== undefined) {
      persistenceRef.current?.markDailyCompleted(state.puzzleNumber);
    }

    // Save final state
    persistenceRef.current?.saveGameState(state);

    // End session
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current);
    }

    // Show result notification
    if (state.gameStatus === 'won') {
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Congratulations!',
        message: `You guessed the word in ${state.guesses.length} ${state.guesses.length === 1 ? 'try' : 'tries'}!`,
        duration: 5000,
      });
    } else {
      addNotification({
        type: NotificationType.INFO,
        title: 'Better luck next time!',
        message: `The word was ${state.targetWord}`,
        duration: 5000,
      });
    }

    setView('completed');
  }, [timer, addNotification]);

  const handlePlayAgain = useCallback(() => {
    if (gameState?.mode === 'daily') {
      // Can't replay daily, go back to mode select
      setView('mode-select');
    } else {
      // Practice mode: start a new game
      startGame('practice');
    }
  }, [gameState?.mode, startGame]);

  const handleBackToMenu = useCallback(() => {
    setView('mode-select');
    setGameState(null);
    setTimer(0);
  }, []);

  const getStats = useCallback(() => {
    return statsServiceRef.current?.getStatistics() || {
      gamesPlayed: 0,
      gamesWon: 0,
      winPercentage: 0,
      currentStreak: 0,
      maxStreak: 0,
      guessDistribution: [0, 0, 0, 0, 0, 0],
      averageGuesses: 0,
    };
  }, []);

  const getShareText = useCallback(() => {
    if (!gameState) return '';
    return engineRef.current.generateShareText(gameState);
  }, [gameState]);

  const getTimeUntilNext = useCallback(() => {
    return engineRef.current.getTimeUntilNextWord();
  }, []);

  // Mode selection config
  const modeConfig: GameModeConfig = {
    gameName: 'Daily Word',
    gameIcon: '🟩',
    availableModes: ['ai', 'local', 'online'],
  };

  const handleModeSelect = useCallback((selection: GameModeSelection) => {
    if (selection.mode === 'ai') {
      startGame('daily');
    } else if (selection.mode === 'local') {
      startGame('practice');
    } else if (selection.mode === 'online') {
      startGame('competitive');
    }
  }, [startGame]);

  const renderContent = () => {
    switch (view) {
      case 'mode-select':
        return (
          <div className="wordle__mode-select">
            <UniversalGameModeSelector
              config={modeConfig}
              onStart={handleModeSelect}
              onCreateRoom={() => startGame('competitive')}
              onJoinRoom={() => startGame('competitive')}
              guestGamesRemaining={isAuthenticated ? undefined : 3}
              isAuthenticated={isAuthenticated}
              isLoading={false}
            />
            <div className="wordle__hard-mode-toggle">
              <label className="wordle__toggle-label">
                <input
                  type="checkbox"
                  checked={hardMode}
                  onChange={(e) => setHardMode(e.target.checked)}
                  className="wordle__toggle-input"
                />
                <span className="wordle__toggle-text">Hard Mode</span>
              </label>
              <span className="wordle__toggle-description">
                Must use revealed hints in subsequent guesses
              </span>
            </div>
          </div>
        );

      case 'playing':
      case 'completed':
        return gameState ? (
          <WordleBoard
            gameState={gameState}
            onKeyPress={handleKeyPress}
            onPlayAgain={handlePlayAgain}
            onBackToMenu={handleBackToMenu}
            onExit={onExit}
            toastMessage={toastMessage}
            shakeRow={shakeRow}
            timer={timer}
            stats={getStats()}
            shareText={getShareText()}
            timeUntilNext={getTimeUntilNext()}
          />
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="wordle">
      <div className="wordle__header">
        <button
          className="wordle__back-btn"
          onClick={view === 'mode-select' ? onExit : handleBackToMenu}
          aria-label="Back"
        >
          ← {view === 'mode-select' ? 'Games' : 'Menu'}
        </button>
        <h1 className="wordle__title">Daily Word</h1>
        <div className="wordle__header-spacer" />
      </div>
      {renderContent()}
    </div>
  );
};

export const WordleGame: React.FC<WordleGameProps> = (props) => (
  <ErrorBoundary componentName="WordleGame">
    <WordleGameInner {...props} />
  </ErrorBoundary>
);
